import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";

const HEADERS = [
  "Code",
  "Pool Name",
  "Number of Clients",
  "Number of Managers",
  "Description",
];

const ClientPoolsPage = () => {
  const navigate = useNavigate();
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchPools = async () => {
    try {
      setLoading(true);
      const res = await api.get("/client-pools");
      setPools(res.data.data || []);
    } catch (error) {
      console.error("Failed to fetch client pools:", error);
      setError("Failed to fetch client pools");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPools();
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Client Pools</h1>
        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={() => navigate("/add-client-pool")}
            className="px-3 py-1.5 font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
          >
            Add Client Pool
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-blue-600" />
        </div>
      )}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!loading && !error && (
        <>
          <p className="text-sm text-gray-600">
            Showing <span className="font-semibold">{pools.length}</span> items.
          </p>

          <div className="w-full bg-white shadow rounded-lg overflow-hidden">
            <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
              <table className="w-full table-auto border-collapse min-w-[600px]">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                    {HEADERS.map((h) => (
                      <th
                        key={h}
                        className="px-3 py-2 text-center font-semibold whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pools.length === 0 ? (
                    <tr>
                      <td
                        colSpan={HEADERS.length}
                        className="py-10 text-center text-gray-400 text-xs"
                      >
                        No client pools found.
                      </td>
                    </tr>
                  ) : (
                    pools.map((pool, index) => {
                      const rowBg = index % 2 === 0 ? "bg-gray-50" : "bg-white";
                      return (
                        <tr
                          key={pool.id}
                          className={`${rowBg} hover:bg-blue-50 transition-colors`}
                        >
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center">
                            <Link
                              to={`/client-pools/${pool.id}`}
                              className="text-blue-600 hover:text-blue-800 underline font-medium"
                            >
                              {`CP-${String(pool.id).padStart(3, "0")}`}
                            </Link>
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-900">
                            {pool.name || "—"}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                            {pool.clients?.length || 0}
                          </td>
                          <td className="px-3 py-2 text-xs whitespace-nowrap text-center text-gray-700">
                            {pool.managers?.length || 0}
                          </td>
                          <td className="px-3 py-2 text-xs text-center text-gray-600">
                            {pool.description || "—"}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ClientPoolsPage;
